import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { LocaleLink } from "@/components/LocaleLink";
import { useAuth } from "@/hooks/useAuth";
import { useProvider } from "@/hooks/useProvider";
import logo from "@/assets/craftfindus-logo.svg";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import { LOCALE_PREFIXES } from "@/i18n";
import { Menu, X, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

const Header = () => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { profile } = useProvider();
  const navigate = useNavigate();
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const prefix = locale || "de";

  const handleSignOut = async () => {
    await signOut();
    navigate(`/${prefix}/`);
  };

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobile();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, closeMobile]);

  const switchLocale = (newPrefix: string) => {
    const currentPath =
      location.pathname.replace(new RegExp(`^/${prefix}`), "") || "/";
    navigate(`/${newPrefix}${currentPath}`);
    closeMobile();
  };

  const navLinks =
    user && profile?.onboarding_completed ? (
      <>
        <LocaleLink
          to="/dashboard"
          className="link-subtle text-sm py-2"
          onClick={closeMobile}
        >
          {t("header.dashboard")}
        </LocaleLink>
        <span className="text-border hidden md:inline">|</span>
        <button
          onClick={() => {
            handleSignOut();
            closeMobile();
          }}
          className="link-subtle text-sm py-2"
        >
          {t("header.signOut")}
        </button>
      </>
    ) : user ? (
      <>
        <LocaleLink
          to="/onboarding"
          className="link-subtle text-sm py-2"
          onClick={closeMobile}
        >
          {t("header.completeProfile")}
        </LocaleLink>
        <span className="text-border hidden md:inline">|</span>
        <button
          onClick={() => {
            handleSignOut();
            closeMobile();
          }}
          className="link-subtle text-sm py-2"
        >
          {t("header.signOut")}
        </button>
      </>
    ) : (
      <>
        <LocaleLink
          to="/inquiry"
          className="link-subtle text-sm py-2"
          onClick={closeMobile}
        >
          {t("header.submitInquiry")}
        </LocaleLink>
        <span className="text-border hidden md:inline">|</span>
        <LocaleLink
          to="/auth"
          className="link-subtle text-sm py-2"
          onClick={closeMobile}
        >
          {t("header.signIn")}
        </LocaleLink>
      </>
    );

  const mobileOverlay = mobileOpen
    ? createPortal(
        <>
          <div
            className="fixed inset-0 top-16 bg-foreground/20 md:hidden z-[60]"
            onClick={closeMobile}
            aria-hidden="true"
          />
          <nav
            className="fixed top-16 left-0 right-0 bottom-0 md:hidden z-[70] bg-background overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation"
          >
            <div className="flex flex-col gap-2 p-6">
              {navLinks}

              {/* Country / language selection */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
                  {t("common.language", "Land & Sprache")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {LOCALE_PREFIXES.map((lp) => (
                    <button
                      key={lp.prefix}
                      onClick={(e) => {
                        e.stopPropagation();
                        switchLocale(lp.prefix);
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border transition-colors ${
                        lp.prefix === prefix
                          ? "border-primary bg-primary/10 text-primary font-medium"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                      }`}
                    >
                      <span>{lp.flag}</span>
                      <span>{lp.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {!user && (
                <div className="mt-6 pt-6 border-t border-border space-y-3">
                  <LocaleLink
                    to="/inquiry"
                    onClick={closeMobile}
                    className="block w-full text-center py-3 bg-primary text-primary-foreground text-sm font-medium"
                  >
                    {t("header.submitInquiry")}
                  </LocaleLink>
                  <LocaleLink
                    to="/registrieren"
                    onClick={closeMobile}
                    className="block w-full text-center py-3 border border-border text-sm"
                  >
                    {t("header.registerAsCraft")}
                  </LocaleLink>
                </div>
              )}
            </div>
          </nav>
        </>,
        document.body
      )
    : null;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm">
        <div className="px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="h-16 md:h-20 flex items-center justify-between border-b border-border">
            <LocaleLink
              to="/"
              className="flex items-center gap-2"
              onClick={closeMobile}
            >
              <img
                src={logo}
                alt="CRAFTFINDUS"
                className="h-20 md:h-24 w-auto"
              />
            </LocaleLink>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-8">{navLinks}</nav>

            <div className="flex items-center gap-1">
              <div className="hidden md:block">
                <LanguageSwitcher />
              </div>
              <span className="text-micro hidden sm:block">
                {user
                  ? profile?.company_name || t("common.craftBusiness")
                  : t("common.forCraftBusinesses")}
              </span>

              <a
                href="tel:+4921224355830"
                className="md:hidden flex items-center justify-center w-11 h-11 text-accent hover:text-accent/80 transition-colors"
                aria-label="Anrufen: 0212 24355830"
              >
                <Phone className="w-5 h-5" />
              </a>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden flex items-center justify-center w-11 h-11 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={
                  mobileOpen ? t("header.menuClose") : t("header.menuOpen")
                }
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOverlay}
    </>
  );
};

export default Header;
